import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLocale, t, tr } from "@/lib/i18n";
import { sanityClient } from "@/lib/sanity";
import { cn } from "@/lib/utils";
import { pickLocalized } from "@/lib/i18n";
import { AppleSignUpButton, GoogleSignUpButton } from "@/components/ui/app-store-buttons";
import { trackStoreClick } from "@/lib/meta-pixel";
import logoMarkup from "@/assets/logo.svg?raw";

// Map English nav labels to internal routes. Sanity-driven labels still
// come from the CMS — this only resolves where the link points.
const ROUTE_MAP: Record<string, string> = {
  home: "/",
  news: "/news",
  faq: "/faq",
  blog: "/blog",
  contact: "/contact",
  pricing: "/pricing",
};

const NAV_QUERY = `*[_type == "navigation"][0]{
  links[enabled == true] | order(order asc){ label, href, order }
}`;

const HERO_CTA_QUERY = `*[_type == "homepage"][0]{
  "ios": heroCTAs.ios{ visible, wording, url },
  "android": heroCTAs.android{ visible, wording, url }
}`;

type NavItem = { to: string; label: string };

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const locale = useLocale();

  const { data: nav } = useQuery({
    queryKey: ["navigation"],
    queryFn: () => sanityClient.fetch<{ links?: any[] }>(NAV_QUERY),
    staleTime: 5 * 60 * 1000,
  });

  const { data: heroCta } = useQuery({
    queryKey: ["homepage-hero-cta"],
    queryFn: () =>
      sanityClient.withConfig({ useCdn: false }).fetch<{
        ios?: { visible?: boolean; wording?: any; url?: string };
        android?: { visible?: boolean; wording?: any; url?: string };
      }>(HERO_CTA_QUERY),
    staleTime: 0,
  });

  const fallback: NavItem[] = [
    { to: "/news", label: tr(t.nav.news, locale) },
    { to: "/faq", label: tr(t.nav.faq, locale) },
    { to: "/blog", label: tr(t.nav.blog, locale) },
    { to: "/contact", label: tr(t.nav.contact, locale) },
  ];

  const navItems: NavItem[] = nav?.links?.length
    ? nav.links
        .map((link: any): NavItem | null => {
          const label = pickLocalized(link.label, locale);
          if (!label) return null;
          // Prefer explicit href, else map by English label key
          const enKey = (link.label?.en || "").toLowerCase().trim();
          const to = link.href || ROUTE_MAP[enKey];
          if (!to) return null;
          return { to, label };
        })
        .filter((x): x is NavItem => x !== null)
    : fallback;

  const search = locale === "en" ? {} : { lang: "fr" as const };
  const ios = heroCta?.ios;
  const android = heroCta?.android;
  const showIosCta = ios?.visible === true;
  const iosLabel = pickLocalized(ios?.wording, locale) || "";
  const iosHref = ios?.url || "#";
  const showAndroidCta = android?.visible === true;
  const androidLabel = pickLocalized(android?.wording, locale) || "";
  const androidHref = android?.url || "#";
  const hasDesktopCta = showIosCta || showAndroidCta;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-secondary-300/50 bg-background/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-[1360px] items-center justify-between px-4 sm:px-6 md:h-[88px] md:px-8">
        <Link
          to="/"
          search={search}
          title="Matchamind — Home"
          className="inline-flex items-center text-primary-500"
        >
          <span
            aria-hidden
            className="block h-7 md:h-8 [&_svg]:h-full [&_svg]:w-auto"
            dangerouslySetInnerHTML={{ __html: logoMarkup }}
          />
          {/* Crawlable anchor text and accessible name for the logo link */}
          <span className="sr-only">Matchamind — Home</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to as any}
              search={search as any}
              className="text-[16px] leading-[24px] text-primary-500 transition-all hover:text-primary-700"
              activeProps={{ className: "underline underline-offset-4" }}
              activeOptions={{ exact: item.to === "/" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className={cn("flex items-center gap-3", !hasDesktopCta && "md:hidden")}>
          {showIosCta && iosLabel && (
            <a
              href={iosHref}
              onClick={() => trackStoreClick("ios", "header")}
              target="_blank"
              rel="noopener noreferrer"
              title="Download Matchamind on the App Store"
              className="hidden md:inline-flex"
            >
              <AppleSignUpButton variant="black" type="button">
                {iosLabel}
              </AppleSignUpButton>
            </a>
          )}
          {showAndroidCta && androidLabel && (
            <a
              href={androidHref}
              onClick={() => trackStoreClick("android", "header")}
              target="_blank"
              rel="noopener noreferrer"
              title="Get Matchamind on Google Play"
              className="hidden md:inline-flex"
            >
              <GoogleSignUpButton variant="white-stroke" type="button">
                {androidLabel}
              </GoogleSignUpButton>
            </a>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-primary-200 text-text-primary md:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-secondary-300/50 bg-background md:hidden">
          <nav className="mx-auto flex max-w-[1360px] flex-col gap-1 px-6 py-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to as any}
                search={search as any}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-[16px] leading-[24px] text-primary-500 transition-all hover:text-primary-700 hover:bg-secondary-200 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
                activeProps={{ className: "underline underline-offset-4" }}
                activeOptions={{ exact: item.to === "/" }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
